import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import {
  SAUDI_BANKS,
  SAUDI_PRIMARY_BANK_CODES,
  SAUDI_BALANCE_TIERS,
  SAUDI_IBAN_PLACEHOLDER,
  getSaudiStatementPeriods,
  bankMatchesCode,
} from './saudiBankingDefaults';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  Plus,
  Calendar,
  Filter,
  Download,
  Upload,
  FileSpreadsheet,
  FileOutput,
  List,
  LayoutList,
  RefreshCcw,
  Trash2,
  Building2,
  TrendingUp,
  FileBarChart,
  ArrowUpFromLine,
  X,
  CheckCircle,
  LayoutGrid,
  Layers,
  Clock,
  DollarSign,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import localApiAdapter from '../../core/services/localApiAdapter';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { mapStatementFromApi, mapStatementToApi, accountsById } from './bankingApiMappers';
import { exportBankingRows, exportBankingPdf, importBankingRows } from './bankingExportImport';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { resolveBanqueEntityReference } from './bankingHelpers';
import { buildBankSelectOptionsFromAccounts } from './bankingSaudiHelpers';
import { normalizeAndSyncBankAccounts } from './bankingTreasurySync';
import { useBanqueConfig, sliceBanqueListItems } from './useBanqueConfig';
import { useSettings } from '../../core/contexts/SettingsContext';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

registerLocale('ar', ar);

const BankStatementsPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.bankStatementsPage.${key}`, opts);
  const fmt = (value) => formatBrandMoney(value);
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const statementPeriodOptions = getSaudiStatementPeriods();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const { settings } = useSettings();
  const bankingConfig = useBanqueConfig();
  const entityLabel = p('entity_label', { defaultValue: 'الكشف البنكي' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'كشوف بنكية' });

  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Advanced options
  const [mainViewMode, setMainViewMode] = useState('all_statements');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const { filters: columnFilters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [columnVisibility, setColumnVisibility] = useState({
    reference: true,
    bankName: true,
    accountNumber: true,
    period: true,
    entryCount: true,
    balance: true,
    uploadDate: true,
  });

  // Data
  const [statements, setStatements] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    reference: '',
    bankName: '',
    accountNumber: '',
    period: '',
    entryCount: '',
    balance: '',
    uploadDate: '',
    account_id: '',
    notes: ''
  });
  
  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  // ===== EFFECTS =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadStatements();
  }, []);

  useEffect(() => {
    if (!showModal || editingRecord || !bankAccounts.length) return;
    setFormData((prev) => {
      if (prev.account_id) return prev;
      const acct = bankAccounts[0];
      return {
        ...prev,
        account_id: String(acct.id),
        bankName: acct.bank_name || prev.bankName,
        accountNumber: acct.account_number || prev.accountNumber,
      };
    });
  }, [showModal, editingRecord, bankAccounts]);

  const accountFormOptions = useMemo(() => bankAccounts.map((a) => ({
    value: String(a.id),
    label: `${a.bank_name || ''} — ${a.account_name || a.account_number}`.trim(),
    isDefault: true,
  })), [bankAccounts]);

  const bankFormOptions = useMemo(
    () => buildBankSelectOptionsFromAccounts(bankAccounts, statements.map((s) => s.bankName)),
    [bankAccounts, statements],
  );

  // ===== HELPER FUNCTIONS =====
  const loadStatements = async () => {
    setLoading(true);
    try {
      const [rows, accounts] = await Promise.all([
        localApiAdapter.get('/banking/statements'),
        localApiAdapter.get('/banking/accounts'),
      ]);
      const acctList = normalizeAndSyncBankAccounts(accounts);
      setBankAccounts(acctList);
      const acctMap = accountsById(acctList);
      setStatements(sortRecordsNewestFirst((Array.isArray(rows) ? rows : []).map((row) => mapStatementFromApi(row, acctMap))));
    } catch (error) {
      console.error('Error loading statements:', error);
      notifyError(showNotify, error.message || p('load_error'));
      setStatements([]);
    } finally {
      setLoading(false);
    }
  };

  const statementExportHeaders = [
    { key: 'reference', label: p('col_code') },
    { key: 'bankName', label: p('th_bank') },
    { key: 'accountNumber', label: p('th_account') },
    { key: 'period', label: p('th_period') },
    { key: 'entryCount', label: p('th_movements') },
    { key: 'balance', label: p('th_balance') },
    { key: 'uploadDate', label: p('th_upload_date') },
  ];

  const handleExportCSV = () => { void exportBankingRows(filteredStatements, statementExportHeaders, 'releves_bancaires', 'csv'); setShowExportDropdown(false); };
  const handleExportExcel = () => { void exportBankingRows(filteredStatements, statementExportHeaders, 'releves_bancaires', 'excel'); setShowExportDropdown(false); };
  const handleExportPDF = () => { exportBankingPdf(p('title')); setShowExportDropdown(false); };
  const handleImport = () => {
    importBankingRows({
      headers: statementExportHeaders,
      schema: { requiredFields: ['accountNumber'], validators: { balance: (v) => !v || !Number.isNaN(parseFloat(v)) } },
      mapRow: (row) => mapStatementToApi({
        bankName: row.bankName,
        accountNumber: row.accountNumber,
        balance: row.balance,
        uploadDate: row.uploadDate || new Date().toISOString().slice(0, 10),
        period: row.period,
        notes: row.period || p('import_default_note'),
      }, bankAccounts),
      postRow: (payload) => localApiAdapter.post('/banking/statements', payload),
      onComplete: loadStatements,
    });
    setShowImportDropdown(false);
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredStatementIds);
    }
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };

  const generateStatementReference = useCallback(
    () => resolveBanqueEntityReference(settings, 'bankStatement', 'BST', statements, (s) => s.reference),
    [settings, statements],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const reference = resolveBanqueEntityReference(
        settings,
        'bankStatement',
        'BST',
        statements,
        (s) => s.reference,
        formData.reference,
      );
      const payload = mapStatementToApi({ ...formData, reference }, bankAccounts);
      if (!payload.account_id) {
        notifyError(showNotify, p('account_required'));
        return;
      }
      if (editingRecord) {
        await localApiAdapter.put(`/banking/statements/${editingRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/banking/statements', payload);
        notifyCreated(showNotify, entityLabel);
      }
      await loadStatements();
      resetForm();
    } catch (error) {
      console.error('Error saving statement:', error);
      notifyError(showNotify, error.message || p('save_error'));
    }
  };

  const handleEdit = (statement) => {
    setEditingRecord(statement);
    setFormData({
      ...statement,
      entryCount: statement.entryCount ?? '',
      balance: statement.balance ?? '',
      account_id: statement.account_id || '',
      notes: statement.notes || '',
    });
    setShowModal(true);
  };

  const handleView = (statement) => {
    setViewingRecord(statement);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('confirm_delete'))) return;
    try {
      await localApiAdapter.delete(`/banking/statements/${id}`);
      notifyDeleted(showNotify, entityLabel);
      await loadStatements();
    } catch (error) {
      console.error('Error deleting statement:', error);
      notifyError(showNotify, error.message || p('delete_error'));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) return;
    try {
      await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`/banking/statements/${id}`)));
      const deleteCount = selectedRecords.length;
      setSelectedRecords([]);
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      await loadStatements();
    } catch (error) {
      console.error('Error bulk deleting statements:', error);
      notifyError(showNotify, error.message || p('delete_error'));
    }
  };

  const resetForm = () => {
    setFormData({ reference: '', bankName: '', accountNumber: '', period: '', entryCount: '', balance: '', uploadDate: '', account_id: '', notes: '' });
    setEditingRecord(null);
    setShowModal(false);
  };

  const openNewStatementModal = () => {
    setEditingRecord(null);
    setFormData({
      reference: generateStatementReference(),
      bankName: '',
      accountNumber: '',
      period: '',
      entryCount: '',
      balance: '',
      uploadDate: new Date().toISOString().slice(0, 10),
      account_id: '',
      notes: '',
    });
    setShowModal(true);
  };

  // Calculate stats
  const stats = {
    total: statements.length,
    banks: [...new Set(statements.map(s => s.bankName))].length,
    movements: statements.reduce((sum, s) => sum + (parseInt(s.entryCount) || 0), 0),
    totalBalance: statements.reduce((sum, s) => sum + (parseFloat(s.balance) || 0), 0),
    recent: statements.filter(s => new Date(s.uploadDate) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length
  };

  // Main tabs
  const mainTabs = [
    { id: 'all_statements', nameKey: 'tab_all', icon: LayoutGrid },
    { id: 'par_banque', nameKey: 'tab_by_bank', icon: Building2 },
    { id: 'par_periode', nameKey: 'tab_by_period', icon: Calendar },
    { id: 'par_solde', nameKey: 'tab_by_balance', icon: DollarSign }
  ];

  const quickFilterButtons = [
    { id: 'all', nameKey: 'filter_all', icon: Layers },
    { id: 'recent', nameKey: 'filter_recent', icon: Clock },
    ...SAUDI_PRIMARY_BANK_CODES.slice(0, 2).map((code) => ({
      id: code,
      name: code,
      icon: Building2,
    })),
  ];

  const statementDistribution = {
    all_statements: {
      titleKey: 'dist_overview',
      data: [
        { filterId: 'recent_7d', nameKey: 'dist_recent_7d', count: stats.recent, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterId: 'total_banks', nameKey: 'dist_total_banks', count: stats.banks, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterId: 'total_statements', nameKey: 'dist_total_statements', count: stats.total, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { filterId: 'movements', nameKey: 'dist_movements', count: stats.movements, color: "bg-gradient-to-r from-amber-500 to-amber-600" }
      ]
    },
    par_banque: {
      titleKey: 'dist_by_bank',
      data: SAUDI_PRIMARY_BANK_CODES.map((code, index) => {
        const bank = SAUDI_BANKS.find((b) => b.code === code);
        const colors = [
          'bg-gradient-to-r from-blue-500 to-blue-600',
          'bg-gradient-to-r from-green-500 to-green-600',
          'bg-gradient-to-r from-purple-500 to-purple-600',
          'bg-gradient-to-r from-amber-500 to-amber-600',
        ];
        return {
          filterId: code,
          name: bank?.name || code,
          count: statements.filter((s) => bankMatchesCode(s.bankName, code)).length,
          color: colors[index],
        };
      }),
    },
    par_periode: {
      titleKey: 'dist_by_period',
      data: statementPeriodOptions.slice(0, 2).map((period, index) => ({
        filterId: period.value,
        name: period.label,
        count: statements.filter((s) => s.period === period.value).length,
        color: index === 0
          ? 'bg-gradient-to-r from-blue-500 to-blue-600'
          : 'bg-gradient-to-r from-purple-500 to-purple-600',
      })),
    },
    par_solde: {
      titleKey: 'dist_by_balance',
      data: [
        { filterId: 'over_2m', nameKey: 'balance_over_2m', count: statements.filter((s) => s.balance >= SAUDI_BALANCE_TIERS.over2m).length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterId: '1m_2m', nameKey: 'balance_1m_2m', count: statements.filter((s) => s.balance >= SAUDI_BALANCE_TIERS.over1m && s.balance < SAUDI_BALANCE_TIERS.over2m).length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterId: 'under_1m', nameKey: 'balance_under_1m', count: statements.filter((s) => s.balance < SAUDI_BALANCE_TIERS.over1m).length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
  };

  const distributionInfo = statementDistribution[mainViewMode] || statementDistribution.all_statements;

  const columnFilterRules = [
    textColumnRule('reference', (row) => row.reference || ''),
    textColumnRule('bankName', (row) => row.bankName),
    textColumnRule('accountNumber', (row) => row.accountNumber),
    textColumnRule('period', (row) => row.period),
    textColumnRule('entryCount', (row) => String(row.entryCount ?? '')),
    textColumnRule('balance', (row) => String(row.balance ?? '')),
    textColumnRule('uploadDate', (row) => (row.uploadDate ? new Date(row.uploadDate).toLocaleDateString(dateLocale) : '')),
  ];

  // Filter statements
  const baseFilteredStatements = statements.filter(statement => {
    if (viewMode === 'cards' && searchTerm) {
      const term = searchTerm.toLowerCase();
      if (
        !statement.reference?.toLowerCase().includes(term) &&
        !statement.accountNumber?.toLowerCase().includes(term) &&
        !statement.bankName?.toLowerCase().includes(term) &&
        !statement.period?.toLowerCase().includes(term)
      ) return false;
    }
    
    if (mainViewMode === 'all_statements') {
      if (quickFilter === 'recent' && new Date(statement.uploadDate) <= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) return false;
      if (SAUDI_PRIMARY_BANK_CODES.includes(quickFilter) && !bankMatchesCode(statement.bankName, quickFilter)) return false;
    }
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_statements':
          if (activeDistributionFilter === 'recent_7d' && new Date(statement.uploadDate) <= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) return false;
          break;
        case 'par_banque':
          if (!bankMatchesCode(statement.bankName, activeDistributionFilter)) return false;
          break;
        case 'par_periode':
          if (activeDistributionFilter !== statement.period) return false;
          break;
        case 'par_solde':
          if (activeDistributionFilter === 'over_2m' && statement.balance < SAUDI_BALANCE_TIERS.over2m) return false;
          if (activeDistributionFilter === '1m_2m' && (statement.balance < SAUDI_BALANCE_TIERS.over1m || statement.balance >= SAUDI_BALANCE_TIERS.over2m)) return false;
          if (activeDistributionFilter === 'under_1m' && statement.balance >= SAUDI_BALANCE_TIERS.over1m) return false;
          break;
      }
    }
    
    return true;
  });

  const filteredStatements = applyColumnFilters(baseFilteredStatements, columnFilters, columnFilterRules)
    .sort((a, b) => b.id - a.id);

  const pagedStatements = useMemo(
    () => sliceBanqueListItems(filteredStatements, bankingConfig.itemsPerPage),
    [filteredStatements, bankingConfig.itemsPerPage],
  );

  const filteredStatementIds = useMemo(() => filteredStatements.map((s) => s.id), [filteredStatements]);
  const allRowsSelected = filteredStatementIds.length > 0 && filteredStatementIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const qawaemStats = useMemo(() => sliceQawaemStats([
    { key: 'total', label: p('stat_total'), value: stats.total, icon: FileText, variant: 'copper' },
    { key: 'banks', label: p('stat_banks'), value: stats.banks, icon: Building2, variant: 'forest' },
    { key: 'movements', label: p('stat_movements'), value: stats.movements, icon: FileBarChart, variant: 'copper' },
    { key: 'recent', label: p('stat_recent'), value: stats.recent, icon: Clock, variant: 'forest' },
  ], 4), [stats, p]);

  const qawaemColumnDefs = useMemo(() => [
    { key: 'reference', label: p('col_code'), checked: columnVisibility.reference !== false, required: true },
    { key: 'bankName', label: p('th_bank'), checked: columnVisibility.bankName !== false },
    { key: 'accountNumber', label: p('th_account'), checked: columnVisibility.accountNumber !== false },
    { key: 'period', label: p('th_period'), checked: columnVisibility.period !== false },
    { key: 'entryCount', label: p('th_movements'), checked: columnVisibility.entryCount !== false },
    { key: 'balance', label: p('th_balance'), checked: columnVisibility.balance !== false },
    { key: 'uploadDate', label: p('th_import_date'), checked: columnVisibility.uploadDate !== false },
    { key: 'actions', label: p('th_actions'), checked: true, required: true },
  ], [columnVisibility, p]);
  const handleToggleColumn = (key) => {
    if (key === 'actions') return;
    const col = qawaemColumnDefs.find((c) => c.key === key);
    if (col?.required) return;
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => columnVisibility[key] !== false;
  const tableColSpan = 1 + ['reference', 'bankName', 'accountNumber', 'period', 'entryCount', 'balance', 'uploadDate'].filter((k) => isColVisible(k)).length + 1;

  return (
    <QawaemErpListPageShell
      className="space-y-3"
      title={p('title')}
      subtitle={p('subtitle')}
      icon={FileText}
      stats={qawaemStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
          title={t('common.delete', { defaultValue: 'حذف' })}
        >
          <Trash2 size={16} />
          <span>{t('common.delete')} ({selectedRecords.length})</span>
        </button>
      ) : null}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder', { defaultValue: 'بحث…' })}
      onCreate={openNewStatementModal}
      createLabel={p('import_button')}
      headerActions={(
        <button
          type="button"
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold flex items-center gap-2`}
        >
          <Filter className="h-5 w-5" />
          <span className="hidden sm:inline">{p('advanced_options')}</span>
        </button>
      )}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import'),
        exportLabel: p('export'),
        onImportCSV: handleImport,
        onImportExcel: handleImport,
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      }}
    >

      {/* Advanced Options */}
      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(tab.nameKey)}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_statements' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.nameKey ? p(filter.nameKey) : filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{p(distributionInfo.titleKey)}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <X size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.filterId || item.nameKey || item.name} onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterId || item.name) ? null : (item.filterId || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterId || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterId || item.name) ? 'text-white' : 'text-white/80'}`}>{item.nameKey ? p(item.nameKey) : item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterId || item.name) ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Main Table */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColVisible('reference') ? (
                    <TableColumnHeaderFilter columnKey="reference" label={p('col_code')} value={columnFilters.reference || ''} onChange={(v) => setFilter('reference', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('bankName') ? (
                    <TableColumnHeaderFilter columnKey="bankName" label={p('th_bank')} value={columnFilters.bankName || ''} onChange={(v) => setFilter('bankName', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('accountNumber') ? (
                    <TableColumnHeaderFilter columnKey="accountNumber" label={p('th_account')} value={columnFilters.accountNumber || ''} onChange={(v) => setFilter('accountNumber', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('period') ? (
                    <TableColumnHeaderFilter columnKey="period" label={p('th_period')} value={columnFilters.period || ''} onChange={(v) => setFilter('period', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('entryCount') ? (
                    <TableColumnHeaderFilter columnKey="entryCount" label={p('th_movements')} value={columnFilters.entryCount || ''} onChange={(v) => setFilter('entryCount', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('balance') ? (
                    <TableColumnHeaderFilter columnKey="balance" label={p('th_balance')} value={columnFilters.balance || ''} onChange={(v) => setFilter('balance', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('uploadDate') ? (
                    <TableColumnHeaderFilter columnKey="uploadDate" label={p('th_import_date')} value={columnFilters.uploadDate || ''} onChange={(v) => setFilter('uploadDate', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('th_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredStatements.length > 0 ? (
                  pagedStatements.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                      {isColVisible('reference') ? (
                        <td className="px-6 py-4"><span className="text-white font-semibold font-mono">{record.reference || '—'}</span></td>
                      ) : null}
                      {isColVisible('bankName') ? (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.bankName}</span></td>
                      ) : null}
                      {isColVisible('accountNumber') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm font-mono">{record.accountNumber}</span></td>
                      ) : null}
                      {isColVisible('period') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.period}</span></td>
                      ) : null}
                      {isColVisible('entryCount') ? (
                        <td className="px-6 py-4 text-center"><span className="text-white font-bold text-lg">{record.entryCount || 0}</span></td>
                      ) : null}
                      {isColVisible('balance') ? (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-bold text-lg">{fmt(record.balance || 0)}</span></div></td>
                      ) : null}
                      {isColVisible('uploadDate') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Clock size={14} className="text-white/50" />
                              {new Date(record.uploadDate).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </div>
                          </div>
                        </td>
                      ) : null}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex items-center justify-center">
                          <QawaemErpRowActions
                          onView={() => handleView(record)}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><FileText size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_count', { count: filteredStatements.length })}</span>
                      <span>{p('footer_movements')} <span className="text-white font-semibold">{filteredStatements.reduce((sum, s) => sum + (parseInt(s.entryCount) || 0), 0)}</span></span>
                      <span>{p('footer_balance')} <span className="text-white font-semibold">{fmt(filteredStatements.reduce((sum, s) => sum + (parseFloat(s.balance) || 0), 0))}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredStatements.length > 0 ? (
              pagedStatements.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-2">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><FileText size={20} className="text-emerald-400" />{record.bankName}</h3>
                      </div>
                      <div className="space-y-2">
                        <div className="text-white/70 text-sm font-mono">{record.accountNumber}</div>
                        <div className="text-white/70 text-sm"><span className="font-medium">{p('card_period')}</span> {record.period}</div>
                        <div className="text-white/70 text-sm"><span className="font-medium">{p('card_movements')}</span> {record.entryCount || 0}</div>
                        <div className="text-white font-bold text-lg">{fmt(record.balance || 0)}</div>
                        <div className="flex items-center gap-1.5 text-white/70 text-sm">
                          <Clock size={14} className="text-white/50" />
                          {p('card_import')} {new Date(record.uploadDate).toLocaleDateString(dateLocale)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-4">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => handleView(record)}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><FileText size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
      </div>

      {/* View Modal — guarded wrap (AGENTS §8) */}
      {showViewModal && viewingRecord ? (
        ReactDOM.createPortal(
          <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)}>
              <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.2 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-white">{p('view_modal_title')}</h2>
                  <button type="button" onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-white/60 text-sm mb-1">{p('label_code')}</p><p className="text-white font-medium font-mono">{viewingRecord.reference || '—'}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_bank')}</p><p className="text-white font-medium">{viewingRecord.bankName}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_account')}</p><p className="text-white font-medium font-mono">{viewingRecord.accountNumber}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('th_period')}</p><p className="text-white font-medium">{viewingRecord.period}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_movement_count')}</p><p className="text-white font-bold text-xl">{viewingRecord.entryCount || 0}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_import_date')}</p><p className="text-white font-medium">{new Date(viewingRecord.uploadDate).toLocaleDateString(dateLocale)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_end_balance')}</p><p className="text-emerald-400 font-bold text-xl">{fmt(viewingRecord.balance || 0)}</p></div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )
      ) : null}

      {/* Add/Edit Modal — tabs (Products.js pattern) */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white">{editingRecord ? p('modal_edit') : p('modal_new')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="statement-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{editingRecord ? p('update') : p('create')}</button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="statement-form" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_code')}</label><input type="text" value={formData.reference} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono" placeholder={p('placeholder_auto_generated')} /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_period_required')} />}</label><CustomSelect value={formData.period} onChange={(value) => setFormData({ ...formData, period: value })} options={statementPeriodOptions.map((opt) => ({ ...opt, isDefault: true }))} searchable={true} creatable={true} autoSelect={false} placeholder={p('placeholder_select')} /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_import_date_required')} />}</label><input type="date" required value={formData.uploadDate} onChange={(e) => setFormData({ ...formData, uploadDate: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_bank_required')} />}</label><CustomSelect value={formData.bankName} onChange={(value) => setFormData({ ...formData, bankName: value })} options={bankFormOptions} searchable={true} creatable={true} autoSelect={false} placeholder={p('placeholder_select')} /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_account_required')} />}</label><CustomSelect value={String(formData.account_id || '')} onChange={(value) => { const acct = bankAccounts.find((a) => String(a.id) === String(value)); setFormData({ ...formData, account_id: value, accountNumber: acct?.account_number || formData.accountNumber, bankName: acct?.bank_name || formData.bankName }); }} options={accountFormOptions} searchable={true} creatable={false} autoSelect={false} placeholder={p('placeholder_select')} /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_account_number')}</label><input type="text" readOnly value={formData.accountNumber} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white/70 font-mono" placeholder={SAUDI_IBAN_PLACEHOLDER} /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_movements_required')} />}</label><CustomNumberInput step={1} value={formData.entryCount || ''} onChange={(value) => setFormData({ ...formData, entryCount: value })} placeholder="0" /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_balance_required')} />}</label><CustomNumberInput step={0.01} value={formData.balance || ''} onChange={(value) => setFormData({ ...formData, balance: value })} placeholder="0.00" /></div>
                  <div className="md:col-span-3"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label><textarea value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_notes')} /></div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </QawaemErpListPageShell>
  );
};

export default BankStatementsPage;


